import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useCallback,
  useRef,
} from 'react';
import {
  MessageWithRelations,
  ConversationSummary,
  MessageType,
  Message,
  MessageUserInfo,
} from '../../../../apis/messaging/messaging.interfaces';
import {
  useGetAllMessagesHook,
  useGetDirectMessagesHook,
  useGetBroadcastMessagesHook,
  useGetConversationsHook,
  useSendDirectMessageHook,
  useSendBroadcastMessageHook,
  useMarkMessagesAsReadHook,
  useMessagingHook,
} from '../hooks/useMessagingHook';
import { localStorageGet } from '../../../../utils/localStorage';

// Typing indicator interface for legacy support
interface TypingIndicatorPayload {
  userId: string;
  conversationId: string;
  isTyping: boolean;
}

export type ConversationFilter = 'all' | 'unread' | 'groups';

type TMessagingState = {
  // Messages
  allMessages: MessageWithRelations[];
  directMessages: MessageWithRelations[];
  broadcastMessages: MessageWithRelations[];

  // Conversations
  conversations: ConversationSummary[];

  // Current selection
  selectedReceiverId: string | null;
  selectedConversationId: string | null;
  selectedClassId: string | null;
  selectedGroupId: string | null;

  // Filters and search
  conversationFilter: ConversationFilter;
  searchQuery: string;

  // Pending new conversation (user selected from search, no message sent yet)
  pendingNewConversation: MessageUserInfo | null;

  // Typing indicators
  typingUsers: Map<string, TypingIndicatorPayload>;

  // Connection
  isConnected: boolean;
  connectionError: string | null;

  // Loading states
  isLoadingMessages: boolean;
  isLoadingConversations: boolean;
  isSendingMessage: boolean;

  // Error states
  error: string | null;
};

enum ACTION_TYPE {
  SET_ALL_MESSAGES = 'SET_ALL_MESSAGES',
  SET_DIRECT_MESSAGES = 'SET_DIRECT_MESSAGES',
  SET_BROADCAST_MESSAGES = 'SET_BROADCAST_MESSAGES',
  SET_CONVERSATIONS = 'SET_CONVERSATIONS',
  ADD_MESSAGE = 'ADD_MESSAGE',
  UPDATE_MESSAGE = 'UPDATE_MESSAGE',
  MARK_MESSAGES_READ = 'MARK_MESSAGES_READ',
  SET_SELECTED_RECEIVER = 'SET_SELECTED_RECEIVER',
  SET_SELECTED_CONVERSATION = 'SET_SELECTED_CONVERSATION',
  SET_SELECTED_CLASS = 'SET_SELECTED_CLASS',
  SET_SELECTED_GROUP = 'SET_SELECTED_GROUP',
  SET_CONVERSATION_FILTER = 'SET_CONVERSATION_FILTER',
  SET_SEARCH_QUERY = 'SET_SEARCH_QUERY',
  SET_PENDING_NEW_CONVERSATION = 'SET_PENDING_NEW_CONVERSATION',
  SET_TYPING_USER = 'SET_TYPING_USER',
  REMOVE_TYPING_USER = 'REMOVE_TYPING_USER',
  SET_CONNECTION_STATUS = 'SET_CONNECTION_STATUS',
  SET_CONNECTION_ERROR = 'SET_CONNECTION_ERROR',
  SET_LOADING_MESSAGES = 'SET_LOADING_MESSAGES',
  SET_LOADING_CONVERSATIONS = 'SET_LOADING_CONVERSATIONS',
  SET_SENDING_MESSAGE = 'SET_SENDING_MESSAGE',
  SET_ERROR = 'SET_ERROR',
  RESET_STATE = 'RESET_STATE',
}

type Action =
  | { type: ACTION_TYPE.SET_ALL_MESSAGES; payload: MessageWithRelations[] }
  | { type: ACTION_TYPE.SET_DIRECT_MESSAGES; payload: MessageWithRelations[] }
  | {
    type: ACTION_TYPE.SET_BROADCAST_MESSAGES;
    payload: MessageWithRelations[];
  }
  | { type: ACTION_TYPE.SET_CONVERSATIONS; payload: ConversationSummary[] }
  | { type: ACTION_TYPE.ADD_MESSAGE; payload: MessageWithRelations }
  | {
    type: ACTION_TYPE.UPDATE_MESSAGE;
    payload: { messageId: string; updates: Partial<MessageWithRelations> };
  }
  | { type: ACTION_TYPE.MARK_MESSAGES_READ; payload: string[] }
  | { type: ACTION_TYPE.SET_SELECTED_RECEIVER; payload: string | null }
  | { type: ACTION_TYPE.SET_SELECTED_CONVERSATION; payload: string | null }
  | { type: ACTION_TYPE.SET_SELECTED_CLASS; payload: string | null }
  | { type: ACTION_TYPE.SET_SELECTED_GROUP; payload: string | null }
  | { type: ACTION_TYPE.SET_CONVERSATION_FILTER; payload: ConversationFilter }
  | { type: ACTION_TYPE.SET_SEARCH_QUERY; payload: string }
  | { type: ACTION_TYPE.SET_PENDING_NEW_CONVERSATION; payload: MessageUserInfo | null }
  | { type: ACTION_TYPE.SET_TYPING_USER; payload: TypingIndicatorPayload }
  | { type: ACTION_TYPE.REMOVE_TYPING_USER; payload: string }
  | { type: ACTION_TYPE.SET_CONNECTION_STATUS; payload: boolean }
  | { type: ACTION_TYPE.SET_CONNECTION_ERROR; payload: string | null }
  | { type: ACTION_TYPE.SET_LOADING_MESSAGES; payload: boolean }
  | { type: ACTION_TYPE.SET_LOADING_CONVERSATIONS; payload: boolean }
  | { type: ACTION_TYPE.SET_SENDING_MESSAGE; payload: boolean }
  | { type: ACTION_TYPE.SET_ERROR; payload: string | null }
  | { type: ACTION_TYPE.RESET_STATE };

const initialState: TMessagingState = {
  allMessages: [],
  directMessages: [],
  broadcastMessages: [],
  conversations: [],
  selectedReceiverId: null,
  selectedConversationId: null,
  selectedClassId: null,
  selectedGroupId: null,
  conversationFilter: 'all',
  searchQuery: '',
  pendingNewConversation: null,
  typingUsers: new Map(),
  isConnected: false,
  connectionError: null,
  isLoadingMessages: false,
  isLoadingConversations: false,
  isSendingMessage: false,
  error: null,
};

const messagingReducer = (
  state: TMessagingState,
  action: Action
): TMessagingState => {
  switch (action.type) {
    case ACTION_TYPE.SET_ALL_MESSAGES:
      return { ...state, allMessages: action.payload };

    case ACTION_TYPE.SET_DIRECT_MESSAGES:
      return { ...state, directMessages: action.payload };

    case ACTION_TYPE.SET_BROADCAST_MESSAGES:
      return { ...state, broadcastMessages: action.payload };

    case ACTION_TYPE.SET_CONVERSATIONS:
      return { ...state, conversations: action.payload };

    case ACTION_TYPE.ADD_MESSAGE:
      const newMessage = action.payload;
      const updatedAllMessages = [newMessage, ...state.allMessages];

      let updatedDirectMessages = state.directMessages;
      let updatedBroadcastMessages = state.broadcastMessages;

      if (newMessage.type === MessageType.DIRECT_MESSAGE) {
        updatedDirectMessages = [newMessage, ...updatedDirectMessages];
      } else if (newMessage.type === MessageType.BROADCAST) {
        updatedBroadcastMessages = [newMessage, ...updatedBroadcastMessages];
      }

      return {
        ...state,
        allMessages: updatedAllMessages,
        directMessages: updatedDirectMessages,
        broadcastMessages: updatedBroadcastMessages,
      };

    case ACTION_TYPE.UPDATE_MESSAGE:
      const { messageId, updates } = action.payload;
      const updateMessageInArray = (messages: MessageWithRelations[]) =>
        messages.map((msg) =>
          msg._id === messageId ? { ...msg, ...updates } : msg
        );

      return {
        ...state,
        allMessages: updateMessageInArray(state.allMessages),
        directMessages: updateMessageInArray(state.directMessages),
        broadcastMessages: updateMessageInArray(state.broadcastMessages),
      };

    case ACTION_TYPE.MARK_MESSAGES_READ:
      const messageIds = action.payload;
      const markAsRead = (messages: MessageWithRelations[]) =>
        messages.map((msg) =>
          messageIds.includes(msg._id || msg.id || '') ? { ...msg, is_read: true } : msg
        );

      return {
        ...state,
        allMessages: markAsRead(state.allMessages),
        directMessages: markAsRead(state.directMessages),
        broadcastMessages: markAsRead(state.broadcastMessages),
      };

    case ACTION_TYPE.SET_SELECTED_RECEIVER:
      return { ...state, selectedReceiverId: action.payload };

    case ACTION_TYPE.SET_SELECTED_CONVERSATION:
      return { ...state, selectedConversationId: action.payload };

    case ACTION_TYPE.SET_SELECTED_CLASS:
      return { ...state, selectedClassId: action.payload };

    case ACTION_TYPE.SET_SELECTED_GROUP:
      return { ...state, selectedGroupId: action.payload };

    case ACTION_TYPE.SET_CONVERSATION_FILTER:
      return { ...state, conversationFilter: action.payload };

    case ACTION_TYPE.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.payload };

    case ACTION_TYPE.SET_PENDING_NEW_CONVERSATION:
      return { ...state, pendingNewConversation: action.payload };

    case ACTION_TYPE.SET_TYPING_USER:
      const typingMap = new Map(state.typingUsers);
      typingMap.set(action.payload.conversationId, action.payload);
      return { ...state, typingUsers: typingMap };

    case ACTION_TYPE.REMOVE_TYPING_USER:
      const updatedTypingMap = new Map(state.typingUsers);
      updatedTypingMap.delete(action.payload);
      return { ...state, typingUsers: updatedTypingMap };

    case ACTION_TYPE.SET_CONNECTION_STATUS:
      return { ...state, isConnected: action.payload };

    case ACTION_TYPE.SET_CONNECTION_ERROR:
      return { ...state, connectionError: action.payload };

    case ACTION_TYPE.SET_LOADING_MESSAGES:
      return { ...state, isLoadingMessages: action.payload };

    case ACTION_TYPE.SET_LOADING_CONVERSATIONS:
      return { ...state, isLoadingConversations: action.payload };

    case ACTION_TYPE.SET_SENDING_MESSAGE:
      return { ...state, isSendingMessage: action.payload };

    case ACTION_TYPE.SET_ERROR:
      return { ...state, error: action.payload };

    case ACTION_TYPE.RESET_STATE:
      return initialState;

    default:
      return state;
  }
};

type TMessagingContextType = {
  state: TMessagingState;

  // Query hooks
  getAllMessages: ReturnType<typeof useGetAllMessagesHook>;
  getDirectMessages: ReturnType<typeof useGetDirectMessagesHook>;
  getBroadcastMessages: ReturnType<typeof useGetBroadcastMessagesHook>;
  getConversations: ReturnType<typeof useGetConversationsHook>;

  // Mutation hooks
  sendDirectMessage: ReturnType<typeof useSendDirectMessageHook>;
  sendBroadcastMessage: ReturnType<typeof useSendBroadcastMessageHook>;
  markMessagesAsRead: ReturnType<typeof useMarkMessagesAsReadHook>;

  // Service methods
  messagingService: ReturnType<typeof useMessagingHook>;

  // Actions
  setSelectedReceiver: (receiverId: string | null) => void;
  setSelectedConversation: (conversationId: string | null) => void;
  setSelectedClass: (classId: string | null) => void;
  setSelectedGroup: (groupId: string | null) => void;
  setConversationFilter: (filter: ConversationFilter) => void;
  setSearchQuery: (query: string) => void;
  setPendingNewConversation: (user: MessageUserInfo | null) => void;
  sendTypingIndicator: (payload: TypingIndicatorPayload) => void;
  sendStopTypingIndicator: (payload: TypingIndicatorPayload) => void;
  resetState: () => void;
};

const MessagingContext = createContext<TMessagingContextType | undefined>(
  undefined
);

export const MessagingProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(messagingReducer, initialState);

  const userParams = { page: 1, limit: 20 };

  // Initialize hooks
  const getAllMessagesQuery = useGetAllMessagesHook(userParams);
  const getDirectMessagesQuery = useGetDirectMessagesHook(
    state.selectedReceiverId || '',
    userParams,
    { enabled: !!state.selectedReceiverId } as Parameters<
      typeof useGetDirectMessagesHook
    >[2]
  );
  const getBroadcastMessagesQuery = useGetBroadcastMessagesHook(
    state.selectedClassId || undefined,
    userParams,
    { enabled: !!state.selectedClassId } as Parameters<
      typeof useGetBroadcastMessagesHook
    >[2]
  );
  const getConversationsQuery = useGetConversationsHook(userParams);

  const sendDirectMessageMutation = useSendDirectMessageHook();
  const sendBroadcastMessageMutation = useSendBroadcastMessageHook();
  const markMessagesAsReadMutation = useMarkMessagesAsReadHook();

  const messagingService = useMessagingHook();

  // Sync query results to state
  const prevLoadingRef = useRef<boolean>(false);
  const prevErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (getAllMessagesQuery.data?.data) {
      dispatch({
        type: ACTION_TYPE.SET_ALL_MESSAGES,
        payload: getAllMessagesQuery.data.data,
      });
    }
    if (getAllMessagesQuery.isLoading !== prevLoadingRef.current) {
      prevLoadingRef.current = getAllMessagesQuery.isLoading;
      dispatch({
        type: ACTION_TYPE.SET_LOADING_MESSAGES,
        payload: getAllMessagesQuery.isLoading,
      });
    }
    if (getAllMessagesQuery.error) {
      const errorMessage =
        (getAllMessagesQuery.error as any)?.response?.data?.message ||
        (getAllMessagesQuery.error as Error)?.message ||
        'Failed to load messages';
      if (errorMessage !== prevErrorRef.current) {
        prevErrorRef.current = errorMessage;
        dispatch({
          type: ACTION_TYPE.SET_ERROR,
          payload: errorMessage,
        });
      }
    } else if (prevErrorRef.current !== null) {
      prevErrorRef.current = null;
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: null,
      });
    }
  }, [
    getAllMessagesQuery.data,
    getAllMessagesQuery.isLoading,
    getAllMessagesQuery.error,
  ]);

  useEffect(() => {
    if (getDirectMessagesQuery.data?.data) {
      dispatch({
        type: ACTION_TYPE.SET_DIRECT_MESSAGES,
        payload: getDirectMessagesQuery.data.data,
      });
    }
  }, [getDirectMessagesQuery.data]);

  useEffect(() => {
    if (getBroadcastMessagesQuery.data?.data) {
      dispatch({
        type: ACTION_TYPE.SET_BROADCAST_MESSAGES,
        payload: getBroadcastMessagesQuery.data.data,
      });
    }
  }, [getBroadcastMessagesQuery.data]);

  useEffect(() => {
    if (getConversationsQuery.data?.data) {
      const convData = getConversationsQuery.data.data;
      dispatch({
        type: ACTION_TYPE.SET_CONVERSATIONS,
        payload: (Array.isArray(convData) ? convData : []) as ConversationSummary[],
      });
    }
    dispatch({
      type: ACTION_TYPE.SET_LOADING_CONVERSATIONS,
      payload: getConversationsQuery.isLoading,
    });
  }, [getConversationsQuery.data, getConversationsQuery.isLoading]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_SENDING_MESSAGE,
      payload:
        sendDirectMessageMutation.isPending ||
        sendBroadcastMessageMutation.isPending,
    });
  }, [
    sendDirectMessageMutation.isPending,
    sendBroadcastMessageMutation.isPending,
  ]);

  // Connection status
  const isConnectedFnRef = useRef(messagingService.isConnected);
  useEffect(() => {
    isConnectedFnRef.current = messagingService.isConnected;
  }, [messagingService.isConnected]);

  useEffect(() => {
    const checkConnection = () => {
      const connected = isConnectedFnRef.current();
      dispatch({ type: ACTION_TYPE.SET_CONNECTION_STATUS, payload: connected });
    };

    checkConnection();
    const interval = setInterval(checkConnection, 5000);

    return () => clearInterval(interval);
  }, []);

  // Real-time listeners
  const messagingServiceRef = useRef(messagingService);
  useEffect(() => {
    messagingServiceRef.current = messagingService;
  }, [messagingService]);

  useEffect(() => {
    const service = messagingServiceRef.current;
    const unsubscribeNewMessage = service.onNewMessage((message) => {
      dispatch({ type: ACTION_TYPE.ADD_MESSAGE, payload: message });
    });

    const unsubscribeMessageSent = service.onMessageSent((message) => {
      dispatch({ type: ACTION_TYPE.ADD_MESSAGE, payload: message });
    });

    // Listen for broadcast messages
    const unsubscribeBroadcast = service.onBroadcast
      ? service.onBroadcast((message) => {
        dispatch({ type: ACTION_TYPE.ADD_MESSAGE, payload: message });
      })
      : () => { };

    const unsubscribeTyping = service.onTyping((indicator) => {
      dispatch({ type: ACTION_TYPE.SET_TYPING_USER, payload: indicator });

      // Auto-remove typing indicator after 3 seconds
      setTimeout(() => {
        dispatch({
          type: ACTION_TYPE.REMOVE_TYPING_USER,
          payload: indicator.conversationId,
        });
      }, 3000);
    });

    const unsubscribeStopTyping = service.onStopTyping((indicator) => {
      dispatch({
        type: ACTION_TYPE.REMOVE_TYPING_USER,
        payload: indicator.conversationId,
      });
    });

    const unsubscribeConnectionSuccess = service.onConnectionSuccess();
    // Connection success handled via onConnect already

    const unsubscribeError = service.onError((error) => {
      dispatch({
        type: ACTION_TYPE.SET_CONNECTION_ERROR,
        payload: error.message,
      });
      dispatch({ type: ACTION_TYPE.SET_ERROR, payload: error.message });
    });

    return () => {
      unsubscribeNewMessage();
      unsubscribeMessageSent();
      unsubscribeBroadcast();
      unsubscribeTyping();
      unsubscribeStopTyping();
      unsubscribeConnectionSuccess();
      unsubscribeError();
    };
  }, []);

  // Handle mutation success
  const getConversationsRefetchRef = useRef(getConversationsQuery.refetch);
  useEffect(() => {
    getConversationsRefetchRef.current = getConversationsQuery.refetch;
  }, [getConversationsQuery.refetch]);

  useEffect(() => {
    if (sendDirectMessageMutation.isSuccess && sendDirectMessageMutation.data) {
      // Get current user ID from JWT token
      let currentUserId = '';
      try {
        const token = localStorageGet('token', '') as string;
        if (token) {
          const payload = token.split('.')[1];
          if (payload) {
            const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(
              payload.length + ((4 - (payload.length % 4)) % 4), '='
            );
            const decoded = JSON.parse(window.atob(padded));
            currentUserId = decoded._id || decoded.id || '';
          }
        }
      } catch { /* ignore */ }

      // Normalize sender_id to match current user's ID
      const messageWithCorrectSender: MessageWithRelations = {
        ...sendDirectMessageMutation.data,
        sender_id: currentUserId || sendDirectMessageMutation.data.sender_id,
      };

      dispatch({
        type: ACTION_TYPE.ADD_MESSAGE,
        payload: messageWithCorrectSender,
      });
      dispatch({ type: ACTION_TYPE.SET_PENDING_NEW_CONVERSATION, payload: null });
      // Refetch conversations to update last message
      getConversationsRefetchRef.current();
    }
  }, [sendDirectMessageMutation.isSuccess, sendDirectMessageMutation.data]);

  useEffect(() => {
    if (
      sendBroadcastMessageMutation.isSuccess &&
      sendBroadcastMessageMutation.data
    ) {
      // Get current user ID from JWT token
      let broadcastUserId = '';
      try {
        const token = localStorageGet('token', '') as string;
        if (token) {
          const payload = token.split('.')[1];
          if (payload) {
            const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(
              payload.length + ((4 - (payload.length % 4)) % 4), '='
            );
            const decoded = JSON.parse(window.atob(padded));
            broadcastUserId = decoded._id || decoded.id || '';
          }
        }
      } catch { /* ignore */ }

      // Normalize sender_id to match current user's ID
      const messageWithCorrectSender: Message = {
        ...sendBroadcastMessageMutation.data,
        sender_id:
          broadcastUserId || sendBroadcastMessageMutation.data.sender_id,
      };

      dispatch({
        type: ACTION_TYPE.ADD_MESSAGE,
        payload: messageWithCorrectSender,
      });
      // Refetch conversations to update last message
      getConversationsRefetchRef.current();
    }
  }, [
    sendBroadcastMessageMutation.isSuccess,
    sendBroadcastMessageMutation.data,
  ]);

  useEffect(() => {
    if (
      markMessagesAsReadMutation.isSuccess &&
      markMessagesAsReadMutation.data
    ) {
      // The mutation response doesn't include message IDs, so we'll need to track them
      // This is a placeholder - you may need to adjust based on your API response
    }
  }, [markMessagesAsReadMutation.isSuccess, markMessagesAsReadMutation.data]);

  // Actions
  const setSelectedReceiver = useCallback((receiverId: string | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_RECEIVER, payload: receiverId });
  }, []);

  const setSelectedConversation = useCallback(
    (conversationId: string | null) => {
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_CONVERSATION,
        payload: conversationId,
      });
    },
    []
  );

  const setSelectedClass = useCallback((classId: string | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_CLASS, payload: classId });
  }, []);

  const setSelectedGroup = useCallback((groupId: string | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_GROUP, payload: groupId });
  }, []);

  const setConversationFilter = useCallback((filter: ConversationFilter) => {
    dispatch({ type: ACTION_TYPE.SET_CONVERSATION_FILTER, payload: filter });
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    dispatch({ type: ACTION_TYPE.SET_SEARCH_QUERY, payload: query });
  }, []);

  const setPendingNewConversation = useCallback(
    (user: MessageUserInfo | null) => {
      dispatch({
        type: ACTION_TYPE.SET_PENDING_NEW_CONVERSATION,
        payload: user,
      });
    },
    []
  );

  const sendTypingIndicator = useCallback(
    (payload: TypingIndicatorPayload) => {
      messagingService.sendTypingIndicator(payload);
    },
    [messagingService]
  );

  const sendStopTypingIndicator = useCallback(
    (payload: TypingIndicatorPayload) => {
      messagingService.sendStopTypingIndicator(payload);
    },
    [messagingService]
  );

  const resetState = useCallback(() => {
    dispatch({ type: ACTION_TYPE.RESET_STATE });
  }, []);

  const contextValue = useMemo<TMessagingContextType>(
    () => ({
      state,
      getAllMessages: getAllMessagesQuery,
      getDirectMessages: getDirectMessagesQuery,
      getBroadcastMessages: getBroadcastMessagesQuery,
      getConversations: getConversationsQuery,
      sendDirectMessage: sendDirectMessageMutation,
      sendBroadcastMessage: sendBroadcastMessageMutation,
      markMessagesAsRead: markMessagesAsReadMutation,
      messagingService,
      setSelectedReceiver,
      setSelectedConversation,
      setSelectedClass,
      setSelectedGroup,
      setConversationFilter,
      setSearchQuery,
      setPendingNewConversation,
      sendTypingIndicator,
      sendStopTypingIndicator,
      resetState,
    }),
    [
      state,
      getAllMessagesQuery,
      getDirectMessagesQuery,
      getBroadcastMessagesQuery,
      getConversationsQuery,
      sendDirectMessageMutation,
      sendBroadcastMessageMutation,
      markMessagesAsReadMutation,
      messagingService,
      setSelectedReceiver,
      setSelectedConversation,
      setSelectedClass,
      setSelectedGroup,
      setConversationFilter,
      setSearchQuery,
      setPendingNewConversation,
      sendTypingIndicator,
      sendStopTypingIndicator,
      resetState,
    ]
  );

  return (
    <MessagingContext.Provider value={contextValue}>
      {children}
    </MessagingContext.Provider>
  );
};

export const useMessagingContext = () => {
  const context = useContext(MessagingContext);
  if (context === undefined) {
    throw new Error(
      'useMessagingContext must be used within a MessagingProvider'
    );
  }
  return context;
};
